import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const FORMAT_BADGE = {
 online: { label: ' Online', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
 in_person: { label: ' In Person', cls: 'bg-brand-50 text-brand-700 border-brand-200' },
 blended: { label: ' Blended', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export default function TrainingPage() {
 const navigate = useNavigate();
 const http = api();
 const [courses, setCourses] = useState([]);
 const [recommended, setRecommended] = useState(null);
 const [loading, setLoading] = useState(true);
 const [filter, setFilter] = useState('all');

 useEffect(() => {
 const load = async () => {
 try {
 const [all, rec] = await Promise.all([
 http.get('/training'),
 http.get('/training/recommended').catch(() => ({ data: null }))
 ]);
 setCourses(all.data || []);
 setRecommended(rec.data);
 } catch { /* silent */ }
 setLoading(false);
 };
 load();
 }, []);

 const certTypes = [...new Set(courses.map(c => c.cert_type))];
 const filtered = filter === 'all' ? courses : courses.filter(c => c.cert_type === filter);

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title="Training Marketplace" description="Get certified and unlock higher-paying shifts on ShiftMatch" />

 <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
 <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
 <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-500">←</button>
 <h1 className="font-bold text-neutral-900"> Training Marketplace</h1>
 </div>
 </div>

 <div className="max-w-3xl mx-auto px-4 py-5">
 {/* Hero */}
 <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 text-white mb-5">
 <h2 className="text-xl font-bold mb-1">Get Certified. Earn More.</h2>
 <p className="text-blue-100 text-sm">Each certification unlocks higher-paying shifts that most workers can't access. Invest in yourself.</p>
 </div>

 {/* Recommended (personalised) */}
 {recommended && recommended.courses && recommended.courses.length > 0 && (
 <div className="mb-5">
 <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
 <span>⚡</span> Recommended For You
 <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
 {recommended.missing_certs.length} certs to unlock
 </span>
 </h3>
 <div className="space-y-2.5">
 {recommended.courses.slice(0, 3).map(course => (
 <div key={course.id} className="bg-white rounded-2xl p-4 border border-amber-200 shadow-sm">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1">
 <div className="flex items-center gap-2 flex-wrap mb-1">
 <h4 className="font-bold text-neutral-900 text-sm">{course.cert_type}</h4>
 <span className={`text-[10px] px-2 py-0.5 rounded-full border ${FORMAT_BADGE[course.format]?.cls || ''}`}>
 {FORMAT_BADGE[course.format]?.label || course.format}
 </span>
 {course.shifts_unlocked > 0 && (
 <span className="text-[10px] px-2 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full font-bold">
 Unlocks {course.shifts_unlocked} shift{course.shifts_unlocked !== 1 ? 's' : ''}
 </span>
 )}
 </div>
 <p className="text-xs text-neutral-500">{course.provider_name}</p>
 <p className="text-xs text-neutral-400 mt-0.5">{course.description}</p>
 {course.duration_hours > 0 && <p className="text-[10px] text-neutral-400 mt-1">⏱ {course.duration_hours}h · {course.location}</p>}
 </div>
 <div className="text-right shrink-0">
 <p className="text-lg font-black text-neutral-900">{course.price > 0 ? `$${parseFloat(course.price).toFixed(0)}` : 'FREE'}</p>
 <a href={course.provider_url} target="_blank" rel="noopener noreferrer"
 className="inline-block mt-2 px-4 py-1.5 bg-brand-500 text-white font-bold text-xs rounded-lg hover:bg-brand-600 transition">
 Enrol →
 </a>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Filter */}
 <div className="flex flex-wrap gap-1.5 mb-4">
 <button onClick={() => setFilter('all')}
 className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${filter === 'all' ? 'bg-brand-500 text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}>
 All Courses
 </button>
 {certTypes.map(ct => (
 <button key={ct} onClick={() => setFilter(ct)}
 className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${filter === ct ? 'bg-brand-500 text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}>
 {ct}
 </button>
 ))}
 </div>

 {/* All courses */}
 {loading ? (
 <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
 ) : (
 <div className="space-y-2.5">
 {filtered.map(course => (
 <div key={course.id} className="bg-white rounded-2xl p-4 border border-neutral-100 shadow-sm hover:shadow-md transition">
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1">
 <div className="flex items-center gap-2 flex-wrap mb-1">
 <h4 className="font-bold text-neutral-900 text-sm">{course.cert_type}</h4>
 <span className={`text-[10px] px-2 py-0.5 rounded-full border ${FORMAT_BADGE[course.format]?.cls || ''}`}>
 {FORMAT_BADGE[course.format]?.label || course.format}
 </span>
 </div>
 <p className="text-xs text-neutral-600 font-medium">{course.provider_name}</p>
 <p className="text-xs text-neutral-400 mt-0.5 line-clamp-2">{course.description}</p>
 <div className="flex gap-3 mt-1.5 text-[10px] text-neutral-400">
 {course.duration_hours > 0 && <span>⏱ {course.duration_hours}h</span>}
 <span> {course.location}</span>
 </div>
 </div>
 <div className="text-right shrink-0">
 <p className="text-lg font-black text-neutral-900">{course.price > 0 ? `$${parseFloat(course.price).toFixed(0)}` : 'FREE'}</p>
 <a href={course.provider_url} target="_blank" rel="noopener noreferrer"
 className="inline-block mt-2 px-4 py-1.5 bg-brand-500 text-white font-bold text-xs rounded-lg hover:bg-brand-600 transition">
 Enrol →
 </a>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}

 <div className="mt-6 p-4 bg-neutral-100 rounded-xl text-center">
 <p className="text-xs text-neutral-500">Can't find what you need? <a href="mailto:support@shiftmatch.work" className="text-brand-600 font-medium">Let us know</a> and we'll add it.</p>
 </div>
 </div>
 </div>
 );
}
